import { useState } from 'react';
import { formatDateTime } from '@belong/common';
import { LinkBase, PrismicImageAdapter, Text } from '@belong/ui';
import clsx from 'clsx';
import { BlogTags } from 'src/features/blog/components/blog-tags/blog-tags';
import { type BlogPost, type BlogPostAuthorData } from 'src/features/blog/types';
import { truncateString } from 'src/utils/truncate';
import { BLOG_STRINGS } from '../../blog.strings';
import { AboutAuthor } from '../about-author/about-author';
import css from './card.module.css';

type BlogCardProps = {
  post: BlogPost;
  variant?: Variant;
  showTopDate?: boolean;
  theme?: 'light' | 'dark';
};

type CardProps = {
  href: string;
  src: string;
  alt: string;
  title: string;
  description: string;
  author?: BlogPostAuthorData;
  date: string;
  tags: string[];
  variant?: Variant;
  showTopDate?: boolean;
  theme?: 'light' | 'dark';
};

export function BlogCard({ post, variant, showTopDate = true, theme }: BlogCardProps): JSX.Element {
  return <Card {...mapPostToCardProps(post)} variant={variant} showTopDate={showTopDate} theme={theme} />;
}

function mapPostToCardProps(post: BlogPost) {
  return {
    href: `/blog/${post.uid}`,
    src: post.data.cover_image.url,
    alt: post.data.cover_image.alt,
    title: post.data.blog_title,
    description: post.data.blog_description,
    author: post.author?.data,
    date: formatDateTime({ dateTime: post.data.sort_by_date, format: 'MMM d, yyyy' }),
    tags: post.data.categories?.map((category: { category: string }) => category?.category || ''),
  };
}

type PageCard = 'page';
type PageReducedCard = 'page-reduced';
type CarouselCard = 'carousel';
type Horizontal = 'horizontal';
type Variant = PageCard | CarouselCard | PageReducedCard | Horizontal;

function Card({
  href,
  src,
  alt,
  title,
  description,
  author,
  date,
  tags,
  variant = 'page',
  showTopDate = true,
  theme = 'light',
}: CardProps): JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const renderOptions = {
    withHashtags: (variant === 'page' || variant === 'horizontal') && !!tags?.length,
    withAuthor: variant !== 'page-reduced' && variant !== 'horizontal',
    withDate: ['page', 'page-reduced', 'horizontal'].includes(variant),
    withBody: variant !== 'page-reduced',
  };

  return (
    <LinkBase
      href={href}
      className={clsx('w-full h-full flex flex-col', variant === 'horizontal' ? 'lg:flex-row lg:gap-lg' : '')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={theme === 'dark' ? css.dark : ''}>
        <div className={clsx(IMAGE_CLASSNAME_MAP[variant], 'relative mb-sm')}>
          {/* TODO: this is just a temporary fix, <img/> tag should be replaced */}
          <div className="w-full h-full">
            <PrismicImageAdapter src={src} alt={alt} className="object-cover rounded" />
          </div>
          <div
            className={clsx(
              'absolute inset-0 border-4 border-solid rounded transition-all duration-hover',
              isHovered ? 'border-green' : 'border-transparent'
            )}
          />
        </div>
        <div>
          {renderOptions.withDate && (
            <Text className={clsx('text-dark-gray my-xs', variant === 'horizontal' && 'mt-0 mb-sm')}>
              {showTopDate && date}
            </Text>
          )}
          <div className="mb-xs">
            <Text {...TITLE_STYLE_MAP[variant]} fontWeight="semibold">
              {truncateString(title, 70)}
            </Text>
          </div>
          {renderOptions.withBody && (
            <div className="mb-xs">
              <Text {...BODY_STYLE_MAP[variant]}>{description}</Text>
            </div>
          )}
          {renderOptions.withHashtags && (
            <div className="mb-sm">
              <BlogTags tags={tags} limitWidth />
            </div>
          )}
          {renderOptions.withAuthor && (
            <>
              <div className="flex-1" /* just in case more spacing is needed */ />
              <div className="mt-sm">
                <AboutAuthor
                  author={author}
                  showImg={variant === 'page'}
                  firstLine={
                    <>
                      <Text as="span">{BLOG_STRINGS['card.writtenBy']}</Text>
                      <Text as="span" fontWeight="semibold">{` ${author?.first_name} ${
                        author?.last_name ? author.last_name : ''
                      }`}</Text>
                    </>
                  }
                  secondLine={
                    <Text {...(variant !== 'page' && { fontWeight: 'semibold' })}>
                      {BLOG_STRINGS['post.lastUpdated']} {date}
                    </Text>
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>
    </LinkBase>
  );
}

const IMAGE_CLASSNAME_MAP: Record<Variant, string> = {
  page: css.imgContainerLg,
  'page-reduced': css.imgContainerLg,
  carousel: css.imgContainerSm,
  horizontal: css.imgContainerHorizontal,
};

type TextStyle = { variant?: 'h3' | 'p1'; className?: string };
type OptionalStyleMap = Partial<Record<Variant, TextStyle>>;

const TITLE_STYLE_MAP: OptionalStyleMap = {
  page: { variant: 'h3', className: 'line-clamp-2' },
  'page-reduced': { variant: 'h3' },
  horizontal: { variant: 'h3', className: 'line-clamp-2' },
};

const BODY_STYLE_MAP: OptionalStyleMap = {
  page: { className: 'line-clamp-3' },
  'page-reduced': { className: 'line-clamp-2' },
  carousel: { variant: 'p1', className: 'line-clamp-3' },
  horizontal: { className: 'line-clamp-3 lg:line-clamp-2' },
};
